/**
 * InvitationsTable — recent admin invitations with delivery / sign-in status
 * and a resend control.
 */
import React from "react";
import { Send } from "lucide-react";
import { fmtDate } from "./helpers";

export default function InvitationsTable({ invitations, resendingId, onResend }) {
  if (invitations.length === 0) return null;
  return (
    <section className="mt-10" data-testid="users-invitations-section">
      <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-zinc-500 mb-3">
        Recent invitations <span className="text-zinc-400 normal-case tracking-normal">({invitations.length})</span>
      </h3>
      <div className="overflow-x-auto bg-white border border-zinc-200">
        <table className="min-w-full text-[13px]">
          <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
            <tr>
              <th className="p-3">Sent</th>
              <th className="p-3">Email</th>
              <th className="p-3">Invited by</th>
              <th className="p-3">Delivery</th>
              <th className="p-3">Sign-in</th>
              <th className="p-3">Resend id</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => {
              const ageDays = inv.age_days;
              const isStale = inv.is_stale;
              const canResend = !inv.has_signed_in;
              return (
              <tr key={inv.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`invite-row-${inv.id}`}>
                <td className="p-3 whitespace-nowrap text-zinc-600">
                  {fmtDate(inv.sent_at)}
                  {isStale ? (
                    <span className="ml-2 inline-block bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide px-1 py-0.5" title={`${ageDays} days old, no sign-in`}>
                      Stale
                    </span>
                  ) : null}
                </td>
                <td className="p-3 font-mono text-zinc-800">{inv.email}</td>
                <td className="p-3 text-zinc-600">{inv.invited_by || "—"}</td>
                <td className="p-3">
                  {inv.sent ? (
                    <span className="inline-block bg-emerald-100 text-emerald-700 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5">Sent</span>
                  ) : (
                    <span className="inline-block bg-amber-100 text-amber-800 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5">Skipped</span>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap">
                  {inv.has_signed_in ? (
                    <span
                      className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5"
                      title={inv.last_login_at ? fmtDate(inv.last_login_at) : ""}
                      data-testid={`invite-signin-${inv.id}`}
                    >
                      ✓ Signed in
                    </span>
                  ) : (
                    <span className="inline-block bg-zinc-100 text-zinc-500 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5" data-testid={`invite-signin-${inv.id}`}>
                      Not yet
                    </span>
                  )}
                </td>
                <td className="p-3 font-mono text-[10.5px] text-zinc-500 break-all max-w-[200px]">{inv.email_id || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canResend ? (
                    <button
                      type="button"
                      onClick={() => onResend(inv)}
                      disabled={resendingId === inv.id}
                      className={`text-[12px] inline-flex items-center gap-1 disabled:opacity-50 ${isStale ? "text-amber-700 hover:text-amber-900" : "text-[#7737F6] hover:text-[#5D28C7]"}`}
                      data-testid={`invite-resend-${inv.id}`}
                      title={isStale ? "More than 7 days old — nudge recommended" : "Re-send this invitation"}
                    >
                      <Send size={12} /> {resendingId === inv.id ? "Sending…" : "Resend"}
                    </button>
                  ) : (
                    <span className="text-zinc-300 text-[11.5px]">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
